import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Paper,
  Title,
  Table,
  Group,
  Button,
  Text,
  Stack,
  SimpleGrid,
  Badge,
  Anchor,
} from "@mantine/core";
import { PageLoader } from "@/components/common";
import { adminApi } from "@/api/client";

const statusFilters = [
  { value: undefined, label: "All" },
  { value: "unevaluated", label: "Unevaluated" },
  { value: "unmatched", label: "Unmatched" },
  { value: "matched", label: "Matched" },
] as const;

export const Route = createFileRoute("/_authenticated/admin/notifications")({
  validateSearch: (search: Record<string, unknown>) => ({
    status: search.status as string | undefined,
  }),
  component: AdminNotificationsPage,
});

function AdminNotificationsPage() {
  const { status } = Route.useSearch();
  const queryClient = useQueryClient();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["admin", "notifications", "stats"],
    queryFn: adminApi.getNotificationStats,
  });

  const { data: apartments, isLoading: apartmentsLoading } = useQuery({
    queryKey: ["admin", "notifications", "apartments", { status }],
    queryFn: () => adminApi.getApartmentsWithMatchCounts(status),
  });

  const reevaluateMutation = useMutation({
    mutationFn: (ids?: number[]) => adminApi.reevaluate(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "notifications"] });
    },
  });

  if (statsLoading || apartmentsLoading) {
    return <PageLoader />;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={1}>Notification Debug</Title>
        <Button
          onClick={() => reevaluateMutation.mutate(undefined)}
          loading={reevaluateMutation.isPending}
        >
          Re-evaluate All
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 2, md: 5 }} spacing="md">
        <Paper
          p="md"
          radius="md"
          withBorder
          style={{ borderColor: "#0f3460", textAlign: "center" }}
        >
          <Text size="xl" fw={700} c="primary">
            {stats?.total_apartments || 0}
          </Text>
          <Text size="sm" c="dimmed">
            Total
          </Text>
        </Paper>
        <Paper
          p="md"
          radius="md"
          withBorder
          style={{ borderColor: "#0f3460", textAlign: "center" }}
        >
          <Text size="xl" fw={700} c="green">
            {stats?.evaluated || 0}
          </Text>
          <Text size="sm" c="dimmed">
            Evaluated
          </Text>
        </Paper>
        <Paper
          p="md"
          radius="md"
          withBorder
          style={{ borderColor: "#0f3460", textAlign: "center" }}
        >
          <Text size="xl" fw={700} c="orange">
            {stats?.unevaluated || 0}
          </Text>
          <Text size="sm" c="dimmed">
            Unevaluated
          </Text>
        </Paper>
        <Paper
          p="md"
          radius="md"
          withBorder
          style={{ borderColor: "#0f3460", textAlign: "center" }}
        >
          <Text size="xl" fw={700} c="primary">
            {stats?.matched || 0}
          </Text>
          <Text size="sm" c="dimmed">
            Matched
          </Text>
        </Paper>
        <Paper
          p="md"
          radius="md"
          withBorder
          style={{ borderColor: "#0f3460", textAlign: "center" }}
        >
          <Text size="xl" fw={700} c="dimmed">
            {stats?.unmatched || 0}
          </Text>
          <Text size="sm" c="dimmed">
            Unmatched
          </Text>
        </Paper>
      </SimpleGrid>

      <Group gap="xs">
        {statusFilters.map((filter) => (
          <Button
            key={filter.value || "all"}
            component={Link}
            to="/admin/notifications"
            search={{ status: filter.value }}
            variant="filled"
            color={status === filter.value ? "primary" : "#0f3460"}
            radius="xl"
            size="sm"
          >
            {filter.label}
          </Button>
        ))}
      </Group>

      <Paper p="md" radius="md" withBorder style={{ borderColor: "#0f3460" }}>
        <Table.ScrollContainer minWidth={800}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ID</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th>Rent</Table.Th>
                <Table.Th>Last Evaluated</Table.Th>
                <Table.Th>Matches</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {apartments?.map(({ apartment, match_count }) => (
                <Table.Tr key={apartment.id}>
                  <Table.Td>{apartment.id}</Table.Td>
                  <Table.Td>
                    <Anchor
                      component={Link}
                      to="/apartments/$id"
                      params={{ id: apartment.id.toString() }}
                      c="primary"
                    >
                      {apartment.title.substring(0, 40)}
                      {apartment.title.length > 40 && "..."}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>{apartment.rent.toLocaleString()} kr</Table.Td>
                  <Table.Td>
                    <Text c="dimmed">
                      {apartment.last_evaluated_at
                        ? new Date(apartment.last_evaluated_at).toLocaleString()
                        : "Never"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={match_count > 0 ? "green" : "gray"} variant="light">
                      {match_count}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="filled"
                      color="#0f3460"
                      onClick={() => reevaluateMutation.mutate([apartment.id])}
                      disabled={reevaluateMutation.isPending}
                    >
                      Re-evaluate
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    </Stack>
  );
}
